import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, newIdempotencyKey, qs } from "../api";
import { Alert, Badge, Button, Card, Chips, DataTable, Empty, ErrorBox, FormError, Loading, Modal, Money, MoneyInput, PageHeader, SelectInput, TextArea, TextInput, useToast, useUnsavedWarning, AFTER_SAVE } from "../ui";
import { date, poundsInput, qtyLabel } from "../format";
import { formatGBP, parsePounds, parseQty } from "../../shared/money";
import { londonDate, monthStart } from "../../shared/dates";
import { FilesPanel } from "../extras";
import { DeleteRecordButton } from "../components";

export function useAccounts() {
  return useQuery({ queryKey: ["accounts"], queryFn: () => api.get("/finance/accounts"), staleTime: 30_000 });
}
export function moneyAccountOptions(accounts: any[] | undefined) {
  return (accounts ?? []).filter((a) => a.is_money && a.active).map((a) => ({ value: a.id, label: a.name }));
}
export function expenseCategoryOptions(accounts: any[] | undefined) {
  return (accounts ?? []).filter((a) => a.is_expense_category && a.active).map((a) => ({ value: a.id, label: a.name }));
}
export function useSuppliers() {
  return useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/suppliers"), staleTime: 30_000 });
}

// ───────── Expenses ─────────
export function ExpenseList() {
  const [params, setParams] = useSearchParams();
  const today = londonDate();
  const from = params.get("from") ?? monthStart(today);
  const to = params.get("to") ?? today;
  const q = useQuery({ queryKey: ["expenses", from, to], queryFn: () => api.get(`/finance/expenses${qs({ from, to })}`) });
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<any>(null);
  const [receipts, setReceipts] = useState<any>(null);
  const rows = q.data?.expenses ?? [];
  const total = rows.filter((e: any) => !e.voided_at).reduce((a: number, e: any) => a + e.amount_pence, 0);
  return (
    <>
      <PageHeader title="Expenses" sub="Day-to-day costs paid straight away. Parts for stock and supplier invoices go under Suppliers → bills; wages under Staff."
        actions={<Button variant="primary" onClick={() => setAdding(true)}>+ Record expense</Button>} />
      <div className="filters">
        <TextInput label="From" type="date" value={from} onChange={(e) => setParams({ from: e.target.value, to }, { replace: true })} />
        <TextInput label="To" type="date" value={to} onChange={(e) => setParams({ from, to: e.target.value }, { replace: true })} />
      </div>
      <Card pad={false}>
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} retry={() => q.refetch()} /> : (
          <DataTable rows={rows} empty={<Empty title="No expenses in this period" />} columns={[
            { key: "d", label: "Date", render: (e: any) => date(e.date) },
            { key: "c", label: "Category", render: (e: any) => e.category },
            { key: "s", label: "Description", render: (e: any) => <>{e.description}{e.supplier_name && <div className="small muted">{e.supplier_name}</div>}</> },
            { key: "p", label: "Paid from", render: (e: any) => e.paid_from },
            { key: "a", label: "Amount", num: true, render: (e: any) => e.voided_at ? <><s><Money pence={e.amount_pence} /></s> <Badge tone="red">void</Badge></> : <Money pence={e.amount_pence} /> },
            { key: "r", label: "Receipt", render: (e: any) => <Button size="sm" variant="ghost" onClick={() => setReceipts(e)}>{e.receipts ? `📎 ${e.receipts}` : "Attach"}</Button> },
            { key: "x", label: "", render: (e: any) => !e.voided_at && <Button size="sm" variant="ghost" onClick={() => setVoiding(e)}>Void</Button> },
          ]} footer={rows.length ? <tr><td colSpan={4} className="right">Total</td><td className="num">{formatGBP(total)}</td><td colSpan={2} /></tr> : undefined} />
        )}
      </Card>
      {adding && <ExpenseModal onClose={() => setAdding(false)} />}
      {receipts && <Modal title={`Receipts — ${receipts.description}`} onClose={() => setReceipts(null)}><FilesPanel ownerType="expense" ownerId={receipts.id} title="Receipts" category="receipt" /></Modal>}
      {voiding && <VoidReasonModal title={`Void expense “${voiding.description}”`} path={`/finance/expenses/${voiding.id}/void`} invalidate={["expenses"]} onClose={() => setVoiding(null)}
        note="The original stays on record and a reversing entry is posted (in the same period if it is still open)." />}
    </>
  );
}

function ExpenseModal({ onClose }: { onClose: () => void }) {
  const accounts = useAccounts();
  const suppliers = useSuppliers();
  const [key] = useState(newIdempotencyKey);
  const [f, setF] = useState({ date: londonDate(), accountId: "", paidFromAccountId: "acc-1010", amount: "", supplierId: "", department: "", description: "" });
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post("/finance/expenses", { ...f, supplierId: f.supplierId || null, department: f.department || null, idempotencyKey: key }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); toast("success", "Expense recorded"); onClose(); },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Record expense" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!f.accountId || !parsePounds(f.amount) || !f.description.trim()} onClick={() => m.mutate()}>Save expense</Button></>}>
      <div className="form-grid">
        <TextInput label="Date" type="date" value={f.date} onChange={set("date")} />
        <MoneyInput label="Amount paid" value={f.amount} onChange={set("amount")} />
        <SelectInput label="Category" value={f.accountId} onChange={set("accountId")} placeholder="Choose…" options={expenseCategoryOptions(accounts.data?.accounts)} />
        <SelectInput label="Paid from" value={f.paidFromAccountId} onChange={set("paidFromAccountId")} options={moneyAccountOptions(accounts.data?.accounts)} />
        <TextInput label="What was it for?" full value={f.description} onChange={set("description")} />
        <SelectInput label="Supplier (optional)" value={f.supplierId} onChange={set("supplierId")} placeholder="—" options={(suppliers.data?.suppliers ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />
        <SelectInput label="Department (optional)" value={f.department} onChange={set("department")} placeholder="Shared" options={[{ value: "mechanical", label: "Mechanical" }, { value: "bodywork", label: "Bodywork" }]} />
      </div>
      <p className="small muted">Receipt photos can be attached once file storage is enabled.</p>
      <FormError error={m.error} />
    </Modal>
  );
}

export function VoidReasonModal({ title, path, invalidate, onClose, note }: { title: string; path: string; invalidate: string[]; onClose: () => void; note?: string }) {
  const [reason, setReason] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({ mutationFn: () => api.post(path, { reason }), onSuccess: () => { invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast("success", "Done"); onClose(); } });
  return (
    <Modal title={title} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" disabled={reason.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Confirm</Button></>}>
      {note && <Alert kind="info">{note}</Alert>}
      <div style={{ marginTop: 12 }}><TextArea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
      <FormError error={m.error} />
    </Modal>
  );
}

// ───────── Suppliers & bills ─────────
export function SupplierList() {
  const q = useSuppliers();
  const bills = useQuery({ queryKey: ["bills", "unpaid"], queryFn: () => api.get("/bills?filter=unpaid") });
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <PageHeader title="Suppliers & bills" actions={<><Button onClick={() => setAdding(true)}>+ Supplier</Button><Link className="btn btn-primary" to="/bills/new">+ Enter supplier bill</Link></>} />
      <div className="grid grid-sidebar">
        <Card title="Suppliers" pad={false}>
          {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} /> : (
            <DataTable rows={q.data.suppliers} rowLink={(s: any) => `/suppliers/${s.id}`} empty={<Empty title="No suppliers yet" action={<Button variant="primary" onClick={() => setAdding(true)}>Add supplier</Button>} />} columns={[
              { key: "n", label: "Supplier", render: (s: any) => <strong>{s.name}</strong> },
              { key: "p", label: "Phone", render: (s: any) => s.phone ?? "—" },
              { key: "b", label: "Bills", num: true, render: (s: any) => s.bill_count },
              { key: "o", label: "We owe", num: true, render: (s: any) => <Money pence={s.balance_pence} /> },
            ]} />
          )}
        </Card>
        <Card title="Unpaid bills" pad={false}>
          {bills.isLoading ? <Loading /> : <DataTable rows={bills.data?.bills ?? []} rowLink={(b: any) => `/bills/${b.id}`} empty={<Empty title="Nothing owed to suppliers" />} columns={[
            { key: "s", label: "Supplier", render: (b: any) => <>{b.supplier_name}<div className="small muted">{b.supplier_ref} · {date(b.bill_date)}</div></> },
            { key: "o", label: "Owed", num: true, render: (b: any) => <Money pence={b.outstanding_pence} /> },
          ]} />}
        </Card>
      </div>
      {adding && <SupplierModal onClose={() => setAdding(false)} onSaved={(id) => navigate(`/suppliers/${id}`)} />}
    </>
  );
}

function SupplierModal({ initial, id, onClose, onSaved }: { initial?: any; id?: string; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState({ name: initial?.name ?? "", phone: initial?.phone ?? "", email: initial?.email ?? "", accountRef: initial?.account_ref ?? "", notes: initial?.notes ?? "" });
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: async () => (id ? (await api.patch(`/suppliers/${id}`, f), { id }) : api.post<{ id: string }>("/suppliers", f)),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["supplier", r.id] }); onSaved(r.id); onClose(); },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={id ? "Edit supplier" : "New supplier"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.name.trim()} loading={m.isPending} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="form-grid">
        <TextInput label="Name" full value={f.name} onChange={set("name")} />
        <TextInput label="Phone" value={f.phone} onChange={set("phone")} />
        <TextInput label="Email" value={f.email} onChange={set("email")} />
        <TextInput label="Our account no. with them" value={f.accountRef} onChange={set("accountRef")} />
        <TextArea label="Notes" full value={f.notes} onChange={set("notes")} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

export function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["supplier", id], queryFn: () => api.get(`/suppliers/${id}`) });
  const [editing, setEditing] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const { supplier: s, bills, balancePence } = q.data;
  return (
    <>
      <PageHeader title={s.name} back={{ to: "/suppliers", label: "Suppliers & bills" }} sub={<>We owe <strong><Money pence={balancePence} /></strong>{s.phone ? ` · ${s.phone}` : ""}</>}
        actions={<><Button onClick={() => setEditing(true)}>Edit</Button><DeleteRecordButton kind="supplier" id={s.id} onDone={() => navigate("/suppliers")} /><Link className="btn btn-primary" to={`/bills/new?supplier=${s.id}`}>+ Enter bill</Link></>} />
      <Card title="Bills" pad={false}>
        <DataTable rows={bills} rowLink={(b: any) => `/bills/${b.id}`} empty={<Empty title="No bills yet" />} columns={[
          { key: "d", label: "Date", render: (b: any) => date(b.bill_date) },
          { key: "r", label: "Their ref", render: (b: any) => b.supplier_ref },
          { key: "t", label: "Total", num: true, render: (b: any) => <Money pence={b.total_pence} /> },
          { key: "o", label: "Outstanding", num: true, render: (b: any) => b.outstanding_pence > 0 ? <Money pence={b.outstanding_pence} className="badge red" /> : <Badge tone="green">Paid</Badge> },
        ]} />
      </Card>
      {editing && <SupplierModal id={s.id} initial={s} onClose={() => setEditing(false)} onSaved={() => {}} />}
    </>
  );
}

interface BillLineEdit { kind: "stock" | "expense" | "asset"; description: string; qty: string; total: string; accountId: string; stockItemId: string }
const blankBillLine = (): BillLineEdit => ({ kind: "stock", description: "", qty: "1", total: "", accountId: "", stockItemId: "" });

export function BillNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const accounts = useAccounts();
  const suppliers = useSuppliers();
  const stock = useQuery({ queryKey: ["stock", ""], queryFn: () => api.get("/stock") });
  const [h, setH] = useState({ supplierId: params.get("supplier") ?? "", supplierRef: "", billDate: londonDate(), dueDate: "", total: "", notes: "" });
  const [lines, setLines] = useState<BillLineEdit[]>([blankBillLine()]);
  const [dupId, setDupId] = useState<string | null>(null);
  useUnsavedWarning(!!h.supplierRef || lines.some((l) => l.description));
  const sum = lines.reduce((a, l) => a + (parsePounds(l.total) ?? 0), 0);
  const total = parsePounds(h.total);
  const m = useMutation({
    mutationFn: () => api.post<{ id: string }>("/bills", { ...h, dueDate: h.dueDate || null, lines: lines.map((l) => ({ kind: l.kind, description: l.description, qty: l.qty, total: l.total, accountId: l.kind === "expense" ? l.accountId : null, stockItemId: l.kind === "stock" ? l.stockItemId || null : null })) }),
    onSuccess: (r) => navigate(`/bills/${r.id}`, AFTER_SAVE),
    onError: (e: any) => setDupId(e?.details?.billId ?? null),
  });
  const set = (i: number, p: Partial<BillLineEdit>) => setLines(lines.map((l, k) => (k === i ? { ...l, ...p } : l)));
  const valid = h.supplierId && h.supplierRef.trim() && total && total === sum && lines.every((l) => l.description.trim() && parsePounds(l.total) !== null && parseQty(l.qty) && (l.kind !== "expense" || l.accountId));
  return (
    <>
      <PageHeader title="Enter supplier bill" back={params.get("supplier") ? { to: `/suppliers/${params.get("supplier")}`, label: "supplier" } : { to: "/suppliers", label: "Suppliers & bills" }} sub="Copy the supplier's invoice. Parts go into stock and are costed when used on a job — not as an expense now." />
      <div className="stack">
        <Card title="Bill details"><div className="form-grid">
          <SelectInput label="Supplier" value={h.supplierId} onChange={(e) => setH({ ...h, supplierId: e.target.value })} placeholder="Choose…" options={(suppliers.data?.suppliers ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />
          <TextInput label="Supplier's invoice number" value={h.supplierRef} onChange={(e) => setH({ ...h, supplierRef: e.target.value })} />
          <TextInput label="Bill date" type="date" value={h.billDate} onChange={(e) => setH({ ...h, billDate: e.target.value })} />
          <TextInput label="Due date (optional)" type="date" value={h.dueDate} onChange={(e) => setH({ ...h, dueDate: e.target.value })} />
          <MoneyInput label="Bill total (as printed)" value={h.total} onChange={(e) => setH({ ...h, total: e.target.value })} hint={total !== null && total !== sum ? `Lines add up to ${formatGBP(sum)}` : undefined} />
        </div></Card>
        <Card title="Lines">
          <div className="table-wrap"><table className="table"><thead><tr><th style={{ width: 150 }}>Type</th><th>Description / item</th><th style={{ width: 80 }}>Qty</th><th style={{ width: 200 }}>Category</th><th style={{ width: 130 }} className="num">Line total £</th><th /></tr></thead><tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td><select className="input" aria-label="Type" value={l.kind} onChange={(e) => set(i, { kind: e.target.value as BillLineEdit["kind"] })}><option value="stock">Parts / stock</option><option value="expense">Expense</option><option value="asset">Equipment</option></select></td>
                <td>
                  {l.kind === "stock" && <select className="input" aria-label="Stock item" value={l.stockItemId} onChange={(e) => { const it = stock.data?.items?.find((x: any) => x.id === e.target.value); set(i, { stockItemId: e.target.value, description: it ? it.name : l.description }); }} style={{ marginBottom: 4 }}>
                    <option value="">New stock item…</option>{(stock.data?.items ?? []).map((it: any) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </select>}
                  <input className="input" aria-label="Description" value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="e.g. Oil filter" />
                </td>
                <td><input className="input" aria-label="Quantity" value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} /></td>
                <td>{l.kind === "expense" ? <select className="input" aria-label="Category" value={l.accountId} onChange={(e) => set(i, { accountId: e.target.value })}><option value="">Choose…</option>{expenseCategoryOptions(accounts.data?.accounts).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select> : <span className="small muted">{l.kind === "stock" ? "Parts stock" : "Equipment & tools"}</span>}</td>
                <td><input className="input right" aria-label="Line total" inputMode="decimal" value={l.total} onChange={(e) => set(i, { total: e.target.value })} /></td>
                <td><Button size="sm" variant="ghost" aria-label="Remove line" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, k) => k !== i))}>✕</Button></td>
              </tr>
            ))}
          </tbody><tfoot><tr><td colSpan={4} className="right">Lines total</td><td className="num">{formatGBP(sum)}</td><td /></tr></tfoot></table></div>
          <Button size="sm" onClick={() => setLines([...lines, blankBillLine()])} style={{ marginTop: 8 }}>+ Add line</Button>
        </Card>
        <div className="form-actions">
          {total !== null && total !== sum && <span className="dirty-note">Lines must add up to the bill total</span>}
          <Button variant="primary" disabled={!valid} loading={m.isPending} onClick={() => m.mutate()}>Save bill</Button>
        </div>
        <FormError error={m.error} />
        {dupId && <Link to={`/bills/${dupId}`}>Open the existing bill</Link>}
      </div>
    </>
  );
}

export function BillDetail() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ["bill", id], queryFn: () => api.get(`/bills/${id}`) });
  const [paying, setPaying] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const { bill: b, lines, payments } = q.data;
  return (
    <>
      <PageHeader title={`Bill ${b.supplier_ref}`} back={{ to: `/suppliers/${b.supplier_id}`, label: b.supplier_name }}
        sub={<>{date(b.bill_date)} · total <Money pence={b.total_pence} /> · outstanding <strong><Money pence={b.outstanding_pence} /></strong></>}
        actions={<>{!b.voided_at && <DeleteRecordButton kind="bill" id={b.id} label="Void bill…" />}{b.outstanding_pence > 0 && <Button variant="primary" onClick={() => setPaying(true)}>Record payment</Button>}</>} />
      {b.voided_at && <div style={{ marginBottom: 12 }}><Alert kind="error" title="This bill is void">{b.void_reason}</Alert></div>}
      <div className="grid grid-sidebar">
        <Card title="Lines" pad={false}>
          <DataTable rows={lines} columns={[
            { key: "k", label: "Type", render: (l: any) => <Badge>{l.kind}</Badge> },
            { key: "d", label: "Description", render: (l: any) => <>{l.description}{l.stock_name && l.stock_name !== l.description && <div className="small muted">Stock: {l.stock_name}</div>}</> },
            { key: "q", label: "Qty", num: true, render: (l: any) => qtyLabel(l.qty_milli) },
            { key: "a", label: "Posted to", render: (l: any) => l.account_name },
            { key: "t", label: "Total", num: true, render: (l: any) => <Money pence={l.total_pence} /> },
          ]} />
        </Card>
        <FilesPanel ownerType="bill" ownerId={b.id} title="Supplier invoice copy" category="receipt" />
        <Card title="Payments">
          {payments.length ? <ul className="timeline">{payments.map((p: any) => <li key={p.id}>{p.voided_at ? <><s><Money pence={p.amount_pence} /></s> <Badge tone="red">void</Badge></> : <Money pence={p.amount_pence} />} <span className="small muted">{date(p.paid_on)} · {p.account_name}</span> {!p.voided_at && <DeleteRecordButton kind="bill_payment" id={p.id} label="Void…" />}</li>)}</ul> : <span className="muted">Not paid yet</span>}
        </Card>
      </div>
      {paying && <BillPaymentModal bill={b} onClose={() => setPaying(false)} />}
    </>
  );
}

function BillPaymentModal({ bill, onClose }: { bill: any; onClose: () => void }) {
  const accounts = useAccounts();
  const [key] = useState(newIdempotencyKey);
  const [f, setF] = useState({ amount: poundsInput(bill.outstanding_pence), accountId: "acc-1010", paidOn: londonDate() });
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({ mutationFn: () => api.post(`/bills/${bill.id}/payments`, { ...f, idempotencyKey: key }), onSuccess: () => { ["bill", "bills", "suppliers", "supplier"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast("success", "Payment recorded"); onClose(); } });
  return (
    <Modal title="Pay supplier bill" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save payment</Button></>}>
      <div className="form-grid">
        <MoneyInput label="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <SelectInput label="Paid from" value={f.accountId} onChange={(e) => setF({ ...f, accountId: e.target.value })} options={moneyAccountOptions(accounts.data?.accounts)} />
        <TextInput label="Date paid" type="date" value={f.paidOn} onChange={(e) => setF({ ...f, paidOn: e.target.value })} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

// ───────── Stock ─────────
export function StockList() {
  const [q, setQ] = useState("");
  const list = useQuery({ queryKey: ["stock", q], queryFn: () => api.get(`/stock${qs({ q })}`) });
  const [adjust, setAdjust] = useState<any>(null);
  const rows = list.data?.items ?? [];
  const value = useMemo(() => rows.reduce((a: number, r: any) => a + (r.value_pence ?? 0), 0), [rows]);
  return (
    <>
      <PageHeader title="Parts stock" sub={<>Stock value at cost: <strong>{formatGBP(value)}</strong>. Parts enter stock from supplier bills and leave when used on an invoiced job.</>}
        actions={<Link className="btn btn-primary" to="/bills/new">+ Enter supplier bill</Link>} />
      <div className="filters"><input className="input" style={{ maxWidth: 320 }} placeholder="Search parts" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search stock" /></div>
      <Card pad={false}>
        {list.isLoading ? <Loading /> : list.isError ? <ErrorBox error={list.error} /> : (
          <DataTable rows={rows} empty={<Empty title="No stock items yet">They are created when you enter a supplier bill with parts.</Empty>} columns={[
            { key: "n", label: "Part", render: (r: any) => <strong>{r.name}</strong> },
            { key: "q", label: "On hand", num: true, render: (r: any) => r.on_hand_milli < 0 ? <Badge tone="red">{qtyLabel(r.on_hand_milli)}</Badge> : qtyLabel(r.on_hand_milli) },
            { key: "c", label: "Avg cost", num: true, render: (r: any) => <Money pence={r.avg_unit_cost_pence} /> },
            { key: "v", label: "Value", num: true, render: (r: any) => <Money pence={r.value_pence} /> },
            { key: "a", label: "", render: (r: any) => <div className="row"><Button size="sm" variant="ghost" onClick={() => setAdjust(r)}>Adjust / return</Button><DeleteRecordButton kind="stock_item" id={r.id} /></div> },
          ]} />
        )}
      </Card>
      {adjust && <StockAdjustModal item={adjust} onClose={() => setAdjust(null)} />}
    </>
  );
}

function StockAdjustModal({ item, onClose }: { item: any; onClose: () => void }) {
  const suppliers = useSuppliers();
  const [f, setF] = useState({ type: "return", qty: "1", reason: "", supplierId: "", unitCost: "", date: londonDate() });
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({ mutationFn: () => api.post(`/stock/${item.id}/adjust`, { ...f, supplierId: f.supplierId || null, unitCost: f.unitCost || null }), onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ["stock"] }); toast("success", `Stock adjusted (${formatGBP(r.valuePence)})`); onClose(); } });
  return (
    <Modal title={`Adjust stock: ${item.name}`} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!f.reason.trim()} onClick={() => m.mutate()}>Save</Button></>}>
      <div className="form-grid">
        <SelectInput label="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} options={[{ value: "return", label: "Return to supplier (credit)" }, { value: "write_off", label: "Write off (damaged / lost)" }, { value: "opening", label: "Opening stock count" }]} />
        <TextInput label="Quantity" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
        {f.type === "return" && <SelectInput label="Supplier" value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })} placeholder="Choose…" options={(suppliers.data?.suppliers ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />}
        {f.type === "opening" && <MoneyInput label="Unit cost" value={f.unitCost} onChange={(e) => setF({ ...f, unitCost: e.target.value })} />}
        <TextInput label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <TextArea label="Reason" full value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}
